import { Tokens } from '@/assets/tokens'
import { Button, Flex, SelectionOption, Selector } from '@/components'
import { useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { Str } from '@/utils/strings'
import { Close, Info, Plus, SmilingFace, Star, Tag } from '@icon-park/react'
import {
  BatchUpdateMentionsRequest,
  EditablePolarityValues,
  PolarityID,
  PolarityValue,
  SearchResponse,
} from '@shared/models'
import { useEffect, useMemo, useState } from 'react'
import { useMentionsContext } from '../../Mentions.context'
import {
  Container,
  EmptyStateText,
  KeyMessageDeleteButton,
  KeyMessageLabel,
  KeyMessageTag,
  KeyMessageText,
  ModalDescription,
  ModalTitle,
  Overlay,
  SectionLabel,
  SectionTitle,
  SpokespersonTag,
  ThemeFormContainer,
  ThemeGroupLabel,
  ThemeTag,
  ThemeTagDeleteButton,
  ThemeTagText,
  WarningBox,
  WarningText,
} from '../EditModal/EditModal.styles'

interface BatchEditModalProps {
  onClose: () => void
}

interface ThemeTag {
  themeInternalID: string
  themeName: string
  themeGroupInternalID: string
  themeGroupName: string
  tempId: string
}

export function BatchEditModal({ onClose }: BatchEditModalProps): JSX.Element {
  const { companyID } = useRouting()
  const {
    selectedMentionIds,
    polarities,
    roles,
    approaches,
    isSearchingRoles,
    searchRoles,
    clearSelection,
    applyFilters,
    mentions,
  } = useMentionsContext()

  const { sendAlert } = Alert.Hook()

  const [themeTagsToAdd, setThemeTagsToAdd] = useState<ThemeTag[]>([])
  const [themeTagsToRemove, setThemeTagsToRemove] = useState<ThemeTag[]>([])
  const [allThemeGroups, setAllThemeGroups] = useState<SearchResponse[]>([])
  const [themesByGroup, setThemesByGroup] = useState<Map<string, SearchResponse[]>>(new Map())
  const [loadingThemesForGroup, setLoadingThemesForGroup] = useState<Map<string, boolean>>(new Map())

  const [keyMessagesToAdd, setKeyMessagesToAdd] = useState<string[]>([])
  const [keyMessagesToRemove, setKeyMessagesToRemove] = useState<string[]>([])
  const [availableKeyMessages, setAvailableKeyMessages] = useState<SelectionOption[]>([])
  const [keyMessagesData, setKeyMessagesData] = useState<any>(null)

  const [spokespersonsToAdd, setSpokespersonsToAdd] = useState<string[]>([])
  const [spokespersonsToRemove, setSpokespersonsToRemove] = useState<string[]>([])
  const [availableSpokespersons, setAvailableSpokespersons] = useState<SelectionOption[]>([])
  const [spokespersonsData, setSpokespersonsData] = useState<any>(null)

  const { binder: bindNewThemeGroup, selectedOption: newThemeGroup, reset: resetNewThemeGroup } = Selector.Binder()
  const { binder: bindNewTheme, selectedOption: newTheme, reset: resetNewTheme } = Selector.Binder()
  const { binder: bindKeyMessage, selectedOption: selectedKeyMessage, reset: resetKeyMessage } = Selector.Binder()
  const { binder: bindSpokesperson, selectedOption: selectedSpokesperson, reset: resetSpokesperson } = Selector.Binder()

  const { binder: bindPolarity, selectedOption: polarity } = Selector.Binder()
  const { binder: bindRole, selectedOption: role } = Selector.Binder()
  const { binder: bindApproach, selectedOption: approach } = Selector.Binder()

  const [searchThemeGroupsAPI, isLoadingThemeGroups] = Api.PromiseQuery<SearchResponse[]>(`theme-groups/search`)
  const [searchThemesAPI] = Api.PromiseQuery<SearchResponse[]>(`themes/search`)
  const [fetchKeyMessages, isLoadingKeyMessages] = Api.PromiseQuery<any>(`key-messages`)
  const [fetchSpokespersons, isLoadingSpokespersons] = Api.PromiseQuery<any>(`spokespersons/search`)

  const { isRunning: isUpdating, run: batchUpdateMentions } = Api.Command<BatchUpdateMentionsRequest>(
    Api.Methods.PATCH,
    `companies/:companyID/mentions/batch`,
  )

  const selectedMentions = useMemo(() => {
    if (!mentions?.elements || selectedMentionIds.size === 0) return []

    const selectedCompositeIds = Array.from(selectedMentionIds)
    const selectedMentions = selectedCompositeIds
      .map((compositeId) => {
        const [postId, brandId] = compositeId.split('_')
        return mentions.elements.find((m) => m.postID === postId && m.brandID.toString() === brandId)
      })
      .filter(Boolean) as typeof mentions.elements

    return selectedMentions
  }, [mentions?.elements, selectedMentionIds])

  const areAllPress = useMemo(() => {
    if (selectedMentions.length <= 1) return true
    return selectedMentions.every((mention) => mention.sourceInternalID === 2)
  }, [selectedMentions])

  const areAllSameSource = useMemo(() => {
    if (selectedMentions.length <= 1) return true
    const firstSourceID = selectedMentions[0].sourceInternalID
    return selectedMentions.every((mention) => mention.sourceInternalID === firstSourceID)
  }, [selectedMentions])

  // Derivado do ID composto (postID_brandID) e não de selectedMentions, que só
  // enxerga a página carregada: o back valida a seleção inteira do mesmo jeito
  // (BatchUpdateMentions.usecase.ts), então calcular em cima do subconjunto
  // visível liberava os campos de tema/mensagem-chave e só estourava no submit.
  const areSameBrand = useMemo(() => {
    if (selectedMentionIds.size <= 1) return true
    const brandIds = new Set(Array.from(selectedMentionIds).map((compositeId) => compositeId.split('_')[1]))
    return brandIds.size <= 1
  }, [selectedMentionIds])

  const allUniqueTags = useMemo(() => {
    if (!areSameBrand || selectedMentions.length === 0) return []

    const seen = new Set<string>()

    return selectedMentions.flatMap((mention) =>
      mention.themeGroup.flatMap((group) =>
        group.themes
          .filter((theme) => {
            if (seen.has(theme.internalID)) return false
            seen.add(theme.internalID)
            return true
          })
          .map((theme) => ({
            themeInternalID: theme.internalID,
            themeName: theme.name,
            themeGroupInternalID: group.internalID,
            themeGroupName: group.name,
            tempId: `${group.internalID}_${theme.internalID}`,
          })),
      ),
    )
  }, [selectedMentions, areSameBrand])

  const allUniqueKeyMessages = useMemo(() => {
    if (!areSameBrand || !areAllSameSource || selectedMentions.length === 0) return []

    const seen = new Set<string>()
    return selectedMentions.flatMap((mention) =>
      mention.keyMessages
        .filter((km) => {
          if (seen.has(km.internalID)) return false
          seen.add(km.internalID)
          return true
        })
        .map((km) => ({ internalID: km.internalID, name: km.name })),
    )
  }, [selectedMentions, areSameBrand, areAllSameSource])

  const allUniqueSpokespersons = useMemo(() => {
    if (!areSameBrand || selectedMentions.length === 0) return []

    const seen = new Set<string>()
    return selectedMentions.flatMap((mention) =>
      mention.spokespersons
        .filter((sp) => {
          if (seen.has(sp.internalID)) return false
          seen.add(sp.internalID)
          return true
        })
        .map((sp) => ({ internalID: sp.internalID, name: sp.name })),
    )
  }, [selectedMentions, areSameBrand])

  useEffect(() => {
    if (areSameBrand) {
      loadThemeGroups()
      loadSpokespersons()
    } else {
      setThemeTagsToAdd([])
      resetNewThemeGroup()
      resetNewTheme()
      setSpokespersonsToAdd([])
      resetSpokesperson()
    }
  }, [areSameBrand])

  const loadThemeGroups = async () => {
    try {
      const mentionCompanyID = selectedMentions[0]?.companyID || companyID
      const [data] = await searchThemeGroupsAPI({
        company: mentionCompanyID,
        size: 10000,
        orderBy: 'name:ASC',
        brandIDs: [...new Set(selectedMentions.map((mention) => mention.brandID))],
      })
      if (data) {
        setAllThemeGroups(data)
      }
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao carregar grupos temáticos',
        message: 'Não foi possível carregar os grupos temáticos.',
      })
    }
  }

  const loadThemesForGroup = async (themeGroupId: string) => {
    if (themesByGroup.has(themeGroupId) || !areSameBrand) return

    setLoadingThemesForGroup((prev) => new Map(prev).set(themeGroupId, true))
    try {
      const brandID = selectedMentions[0]?.brandID
      const mentionCompanyID = selectedMentions[0]?.companyID || companyID
      if (!brandID) return

      const [data] = await searchThemesAPI({
        company: mentionCompanyID,
        marca: brandID,
        grupoTematico: themeGroupId.toString(),
        size: 10000,
        fathersOnly: true,
        orderBy: 'value:ASC',
      })
      if (data) {
        setThemesByGroup((prev) => new Map(prev).set(themeGroupId, data))
      }
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao carregar temas',
        message: `Não foi possível carregar os temas para o grupo selecionado.`,
      })
    } finally {
      setLoadingThemesForGroup((prev) => new Map(prev).set(themeGroupId, false))
    }
  }

  const availableThemeGroups = useMemo(() => {
    if (!areSameBrand) return []
    return allThemeGroups
  }, [allThemeGroups, areSameBrand])

  const availableThemesForNewGroup = useMemo(() => {
    if (!newThemeGroup || !areSameBrand) return []
    const allThemes = themesByGroup.get(String(newThemeGroup.value)) || []
    const addedThemeIds = themeTagsToAdd.map((tag) => tag.themeInternalID)
    return allThemes.filter((theme) => !addedThemeIds.includes(String(theme.value)))
  }, [newThemeGroup, themesByGroup, areSameBrand, themeTagsToAdd])

  useEffect(() => {
    if (newThemeGroup && areSameBrand) {
      loadThemesForGroup(String(newThemeGroup.value))
      resetNewTheme()
    } else {
      resetNewTheme()
    }
  }, [newThemeGroup, areSameBrand])

  const handleAddTheme = () => {
    if (!areSameBrand) return

    if (!newThemeGroup || !newTheme) {
      sendAlert({
        type: 'error',
        title: 'Seleção incompleta',
        message: 'Selecione um grupo temático e um tema antes de adicionar.',
      })
      return
    }

    const themeName = availableThemesForNewGroup.find((t) => t.value === String(newTheme.value))?.name || newTheme.text

    const newTag: ThemeTag = {
      themeInternalID: String(newTheme.value),
      themeName: themeName,
      themeGroupInternalID: String(newThemeGroup.value),
      themeGroupName: newThemeGroup.text,
      tempId: `temp_${Date.now()}_${Math.random()}`,
    }

    setThemeTagsToAdd((prev) => [...prev, newTag])
    resetNewThemeGroup()
    resetNewTheme()
  }

  const handleRemoveTheme = (tempId: string) => {
    if (!areSameBrand) return
    setThemeTagsToAdd((prev) => prev.filter((tag) => tag.tempId !== tempId))
  }

  const handleRemoveCommonTheme = (tempId: string) => {
    const tag = allUniqueTags.find((t) => t.tempId === tempId)
    if (!tag) return

    const alreadyMarked = themeTagsToRemove.some((t) => t.tempId === tempId)
    if (!alreadyMarked) {
      setThemeTagsToRemove((prev) => [...prev, tag])
    }
  }

  const handleRemoveExistingKeyMessage = (internalID: string) => {
    setKeyMessagesToRemove((prev) => [...prev, internalID])
  }

  const handleRemoveExistingSpokesperson = (internalID: string) => {
    setSpokespersonsToRemove((prev) => [...prev, internalID])
  }

  useEffect(() => {
    loadKeyMessages()
  }, [])

  const loadKeyMessages = async () => {
    // selectedMentions só enxerga a página carregada: com a seleção toda em
    // outra página ele fica vazio e o [0] abaixo estourava TypeError.
    if (!areSameBrand || !areAllSameSource || selectedMentions.length === 0) return
    try {
      const [data] = await fetchKeyMessages({
        company: companyID!,
        size: 10000,
        orderBy: 'message:ASC',
        brandIDs: [...new Set(selectedMentions.map((mention) => mention.brandID))],
        sourceInternalID: selectedMentions[0].sourceInternalID,
      })
      if (data) {
        setKeyMessagesData(data)
      }
    } catch (error) {
      console.error('Error loading key messages:', error)
    }
  }

  const loadSpokespersons = async () => {
    if (!areSameBrand) return
    try {
      const [data] = await fetchSpokespersons({
        company: companyID,
        size: 10000,
        brandIDs: [...new Set(selectedMentions.map((mention) => mention.brandID))],
      })
      if (data) {
        setSpokespersonsData(data)
      }
    } catch (error) {
      console.error('Error loading key messages:', error)
    }
  }

  useEffect(() => {
    if (!areSameBrand || !areAllSameSource) return
    if (keyMessagesData) {
      const messages = keyMessagesData.elements || []
      const activeMessages = messages.filter((msg: any) => msg.isActive)

      const options: SelectionOption[] = activeMessages.map((msg: any) => ({
        text: msg.message,
        value: msg.internalID,
      }))

      setAvailableKeyMessages(options)
    }
  }, [keyMessagesData])

  useEffect(() => {
    if (!areSameBrand) return
    if (spokespersonsData) {
      const spokespersons = spokespersonsData || []

      const options: SelectionOption[] = spokespersons.map((sp: any) => ({
        text: sp.text,
        value: sp.value,
      }))

      setAvailableSpokespersons(options)
    }
  }, [spokespersonsData])

  const handleAddKeyMessage = () => {
    if (!areSameBrand || !areAllSameSource) return

    if (!selectedKeyMessage) {
      sendAlert({
        type: 'error',
        title: 'Seleção incompleta',
        message: 'Selecione uma mensagem-chave antes de adicionar.',
      })
      return
    }

    const internalID = String(selectedKeyMessage.value)

    if (keyMessagesToAdd.includes(internalID)) {
      sendAlert({
        type: 'error',
        title: 'Mensagem duplicada',
        message: 'Esta mensagem-chave já foi adicionada.',
      })
      return
    }

    setKeyMessagesToAdd((prev) => [...prev, internalID])
    resetKeyMessage()
  }

  const handleAddSpokesperson = () => {
    if (!areSameBrand || !areAllSameSource) return

    if (!selectedSpokesperson) {
      sendAlert({
        type: 'error',
        title: 'Seleção incompleta',
        message: 'Selecione uma mensagem-chave antes de adicionar.',
      })
      return
    }

    const internalID = String(selectedSpokesperson.value)

    if (spokespersonsToAdd.includes(internalID)) {
      sendAlert({
        type: 'error',
        title: 'Mensagem duplicada',
        message: 'Esta mensagem-chave já foi adicionada.',
      })
      return
    }

    setSpokespersonsToAdd((prev) => [...prev, internalID])
    resetSpokesperson()
  }

  const handleRemoveKeyMessage = (removeInternalID: string) => {
    if (!areSameBrand) return

    setKeyMessagesToAdd((prev) => prev.filter((internalID) => internalID !== removeInternalID))
  }

  const handleRemoveSpokesperson = (removeInternalID: string) => {
    if (!areSameBrand) return

    setSpokespersonsToAdd((prev) => prev.filter((internalID) => internalID !== removeInternalID))
  }

  const handleBatchUpdate = async () => {
    const updates: any = {}

    // Também vem do ID composto: selectedMentions pode estar vazio quando as
    // menções marcadas estão em outra página da listagem.
    if (areSameBrand) {
      const [selectedCompositeId] = Array.from(selectedMentionIds)
      if (selectedCompositeId) updates.brandID = selectedCompositeId.split('_')[1]
    }

    if (polarity) updates.polarityInternalID = polarity.value as number
    if (role) updates.roleInternalID = role.value as number
    if (approach && areAllPress) updates.approachInternalID = approach.value as number

    if (areSameBrand && themeTagsToAdd.length > 0) {
      updates.themeAdditions = themeTagsToAdd.map((tag) => ({
        themeGroupInternalID: tag.themeGroupInternalID,
        themeInternalID: tag.themeInternalID,
      }))
    }

    if (areSameBrand && themeTagsToRemove.length > 0) {
      updates.themeRemovals = themeTagsToRemove.map((tag) => ({
        themeGroupInternalID: tag.themeGroupInternalID,
        themeInternalID: tag.themeInternalID,
        mentionIds: Array.from(selectedMentionIds).filter((compositeId) => {
          const [postId, brandId] = compositeId.split('_')
          const mention = selectedMentions.find((m) => m.postID === postId && m.brandID.toString() === brandId)
          return mention?.themeGroup.some((g) => g.themes.some((t) => t.internalID === tag.themeInternalID))
        }),
      }))
    }

    if (areSameBrand && areAllSameSource && keyMessagesToRemove.length > 0) {
      updates.keyMessageRemovals = keyMessagesToRemove.map((internalID) => ({
        keyMessageInternalID: internalID,
        mentionIds: Array.from(selectedMentionIds).filter((compositeId) => {
          const [postId, brandId] = compositeId.split('_')
          const mention = selectedMentions.find((m) => m.postID === postId && m.brandID.toString() === brandId)
          return mention?.keyMessages.some((km) => km.internalID === internalID)
        }),
      }))
    }

    if (areSameBrand && spokespersonsToRemove.length > 0) {
      updates.spokespersonRemovals = spokespersonsToRemove.map((internalID) => ({
        spokespersonInternalID: internalID,
        mentionIds: Array.from(selectedMentionIds).filter((compositeId) => {
          const [postId, brandId] = compositeId.split('_')
          const mention = selectedMentions.find((m) => m.postID === postId && m.brandID.toString() === brandId)
          return mention?.spokespersons.some((sp) => sp.internalID === internalID)
        }),
      }))
    }

    if (areSameBrand && spokespersonsToAdd.length > 0) {
      updates.spokespersonAdditions = spokespersonsToAdd.map((spokespersonInternalID) => ({
        spokespersonInternalID,
      }))
    }

    if (keyMessagesToAdd.length > 0) {
      updates.keyMessageAdditions = keyMessagesToAdd.map((keyMessageInternalID) => ({
        keyMessageInternalID,
      }))
    }

    const hasUpdates =
      polarity ||
      role ||
      (approach && areAllPress) ||
      updates.themeAdditions?.length > 0 ||
      updates.themeRemovals?.length > 0 ||
      updates.keyMessageAdditions?.length > 0 ||
      updates.keyMessageRemovals?.length > 0 ||
      updates.spokespersonAdditions?.length > 0 ||
      updates.spokespersonRemovals?.length > 0

    if (!hasUpdates) {
      sendAlert({
        type: 'error',
        title: 'Nenhuma alteração',
        message: 'Selecione pelo menos um campo para atualizar.',
      })
      return
    }

    try {
      const mentionIdsForBackend = Array.from(selectedMentionIds)

      const requestBody = {
        mentionIds: mentionIdsForBackend,
        updates,
      }

      batchUpdateMentions({
        input: requestBody,
        binds: [['companyID', companyID!]],
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: 'Menções atualizadas',
            message: `${selectedMentionIds.size} menções foram atualizadas com sucesso.`,
          })

          clearSelection()
          applyFilters()
          onClose()
        },
        onFail: (error) => {
          sendAlert({
            type: 'error',
            title: 'Erro ao atualizar menções',
            message: `Não foi possível atualizar as menções selecionadas: ${error.message}`,
          })
        },
      })
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao atualizar menções',
        message: 'Não foi possível atualizar as menções selecionadas.',
      })
    }
  }

  return (
    <Overlay>
      <Container style={{ width: '60rem' }}>
        <Flex $type="row" $justify="center" $align="center" $fit style={{ position: 'relative' }}>
          <ModalTitle>Edição em Lote</ModalTitle>
          <Button.Root $variant="link" onClick={onClose} style={{ position: 'absolute', right: 0 }}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </Flex>

        <ModalDescription>{selectedMentionIds.size} menções selecionadas.</ModalDescription>

        {selectedMentions.length > 1 && !areAllPress && (
          <WarningBox>
            <Button.Icon value={Info} color="#856404" />
            <WarningText>
              Pelo menos uma das postagens que você selecionou é de redes, portanto não é possível alterar o formato.
            </WarningText>
          </WarningBox>
        )}

        <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={16}>
          <Selector.Default
            {...bindPolarity}
            placeholder="Polaridade"
            options={
              polarities
                ?.filter((item) => EditablePolarityValues.includes(item.value as PolarityValue))
                .map((item) => ({
                  text: Str.from(item.text).initCap,
                  value: PolarityID.get(item.value as PolarityValue) ?? 0,
                })) ?? []
            }>
            <Selector.Icon value={SmilingFace} color={Tokens.ColorUIPrimary} />
          </Selector.Default>

          <Selector.Searchable
            {...bindRole}
            placeholder={'Abordagem'}
            options={roles?.map((item) => ({ text: Str.from(item.text).initCap, value: item.value })) ?? []}
            onSearch={(searchParam: string): void => searchRoles({ name: searchParam })}
            loading={isSearchingRoles}>
            <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
          </Selector.Searchable>

          <Selector.Default
            {...bindApproach}
            placeholder={'Formato'}
            options={
              areAllPress ?
                (approaches?.map((item) => ({ text: Str.from(item.text).initCap, value: item.value })) ?? [])
              : []
            }
            loading={false}>
            <Selector.Icon value={Tag} color={!areAllPress ? Tokens.ColorTextTertiary : Tokens.ColorUIPrimary} />
          </Selector.Default>
        </Flex>

        <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
          <SectionTitle>Porta-voz</SectionTitle>
          {selectedMentionIds.size > 1 && !areSameBrand && (
            <WarningBox>
              <Button.Icon value={Info} color="#856404" />
              <WarningText>
                Só é possível editar ports-voz quando todas as menções selecionadas forem da mesma marca.
              </WarningText>
            </WarningBox>
          )}
          <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={12}>
            <Flex $type="column" $fit style={{ flex: 1, maxWidth: '400px' }}>
              <Selector.Default
                disabled={!areSameBrand}
                {...bindSpokesperson}
                options={availableSpokespersons}
                placeholder="Selecione um porta-voz"
                loading={isLoadingSpokespersons}
              />
            </Flex>
            <Button.Root
              disabled={!areSameBrand && !selectedSpokesperson}
              $variant="secondary"
              onClick={handleAddSpokesperson}>
              <Button.Icon value={Plus} />
              <Button.Text value="Adicionar" />
            </Button.Root>
          </Flex>

          {allUniqueSpokespersons.length > 0 && areSameBrand && (
            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={8}>
              <KeyMessageLabel>Porta-vozes das postagens selecionadas:</KeyMessageLabel>
              <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={8} style={{ flexWrap: 'wrap' }}>
                {allUniqueSpokespersons
                  .filter((sp) => !spokespersonsToRemove.includes(sp.internalID))
                  .map((sp) => (
                    <SpokespersonTag key={sp.internalID}>
                      <KeyMessageText title={sp.name}>👤 {sp.name}</KeyMessageText>
                      <KeyMessageDeleteButton
                        onClick={() => handleRemoveExistingSpokesperson(sp.internalID)}
                        title="Remover porta-voz">
                        ×
                      </KeyMessageDeleteButton>
                    </SpokespersonTag>
                  ))}
              </Flex>
            </Flex>
          )}

          {spokespersonsToAdd.length > 0 && areSameBrand && (
            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={8}>
              <KeyMessageLabel>Porta-voz para adicionar:</KeyMessageLabel>
              <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={8} style={{ flexWrap: 'wrap' }}>
                {spokespersonsToAdd.map((internalID) => {
                  const message = availableSpokespersons.find((msg) => msg.value === internalID)
                  return (
                    <SpokespersonTag key={internalID}>
                      <KeyMessageText title={message?.text}>👤 {message?.text}</KeyMessageText>
                      <KeyMessageDeleteButton
                        onClick={() => handleRemoveSpokesperson(internalID)}
                        title="Remover porta-voz">
                        ×
                      </KeyMessageDeleteButton>
                    </SpokespersonTag>
                  )
                })}
              </Flex>
            </Flex>
          )}

          {spokespersonsToAdd.length === 0 && allUniqueSpokespersons.length === 0 && (
            <EmptyStateText>Nenhum porta-voz selecionado. Os porta-vozes existentes serão mantidos.</EmptyStateText>
          )}
        </Flex>

        <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
          <SectionTitle>Mensagens-Chave</SectionTitle>
          {selectedMentionIds.size > 1 && (!areSameBrand || !areAllSameSource) && (
            <WarningBox>
              <Button.Icon value={Info} color="#856404" />
              <WarningText>
                Só é possível editar mensagem chave quando todas as menções selecionadas forem da mesma marca e da mesma
                fonte (redes ou imprensa).
              </WarningText>
            </WarningBox>
          )}
          <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={12}>
            <Flex $type="column" $fit style={{ flex: 1, maxWidth: '400px' }}>
              <Selector.Default
                disabled={!areSameBrand || !areAllSameSource}
                {...bindKeyMessage}
                options={availableKeyMessages}
                placeholder="Selecione uma mensagem-chave"
                loading={isLoadingKeyMessages}
              />
            </Flex>
            <Button.Root
              disabled={(!areSameBrand || !areAllSameSource) && !selectedKeyMessage}
              $variant="secondary"
              onClick={handleAddKeyMessage}>
              <Button.Icon value={Plus} />
              <Button.Text value="Adicionar" />
            </Button.Root>
          </Flex>

          {allUniqueKeyMessages.length > 0 && areSameBrand && areAllSameSource && (
            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={8}>
              <KeyMessageLabel>Mensagens-chave das postagens selecionadas:</KeyMessageLabel>
              <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={8} style={{ flexWrap: 'wrap' }}>
                {allUniqueKeyMessages
                  .filter((km) => !keyMessagesToRemove.includes(km.internalID))
                  .map((km) => (
                    <KeyMessageTag key={km.internalID}>
                      <KeyMessageText title={km.name}>{km.name}</KeyMessageText>
                      <KeyMessageDeleteButton
                        onClick={() => handleRemoveExistingKeyMessage(km.internalID)}
                        title="Remover mensagem-chave">
                        ×
                      </KeyMessageDeleteButton>
                    </KeyMessageTag>
                  ))}
              </Flex>
            </Flex>
          )}

          {keyMessagesToAdd.length > 0 && areSameBrand && (
            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={8}>
              <KeyMessageLabel>Mensagens-chave para adicionar:</KeyMessageLabel>
              <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={8} style={{ flexWrap: 'wrap' }}>
                {keyMessagesToAdd.map((internalID) => {
                  const message = availableKeyMessages.find((msg) => msg.value === internalID)
                  return (
                    <KeyMessageTag key={internalID}>
                      <KeyMessageText title={message?.text}>{message?.text || `Mensagem ${internalID}`}</KeyMessageText>
                      <KeyMessageDeleteButton
                        onClick={() => handleRemoveKeyMessage(internalID)}
                        title="Remover mensagem-chave">
                        ×
                      </KeyMessageDeleteButton>
                    </KeyMessageTag>
                  )
                })}
              </Flex>
            </Flex>
          )}

          {keyMessagesToAdd.length === 0 && allUniqueKeyMessages.length === 0 && (
            <EmptyStateText>
              Nenhuma mensagem-chave selecionada. As mensagens-chave existentes serão mantidas.
            </EmptyStateText>
          )}
        </Flex>

        <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
          <SectionTitle $disabled={!areSameBrand}>Temas </SectionTitle>
          {selectedMentionIds.size > 1 && !areSameBrand && (
            <WarningBox>
              <Button.Icon value={Info} color="#856404" />
              <WarningText>
                Só é possível editar temas quando todas as menções selecionadas forem da mesma marca.
              </WarningText>
            </WarningBox>
          )}
          <ThemeFormContainer $disabled={!areSameBrand}>
            <SectionLabel $disabled={!areSameBrand}>Adicionar Novo Tema</SectionLabel>

            <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={12}>
              <Flex $type="column" $fit style={{ flex: 1 }}>
                <Selector.Default
                  {...bindNewThemeGroup}
                  placeholder={
                    !areSameBrand ? 'Desabilitado - marcas diferentes'
                    : availableThemeGroups.length === 0 ?
                      'Nenhum grupo disponível'
                    : 'Selecionar Grupo Temático'
                  }
                  options={availableThemeGroups.map((group) => ({
                    text: Str.from(group.text).initCap,
                    value: group.value,
                  }))}
                  loading={isLoadingThemeGroups}
                  disabled={!areSameBrand}>
                  <Selector.Icon value={Tag} color={!areSameBrand ? Tokens.ColorTextTertiary : Tokens.ColorUIPrimary} />
                </Selector.Default>
              </Flex>

              <Flex $type="column" $fit style={{ flex: 1 }}>
                <Selector.Default
                  {...bindNewTheme}
                  placeholder={
                    !areSameBrand ? 'Desabilitado - marcas diferentes'
                    : !newThemeGroup ?
                      'Selecione um grupo primeiro'
                    : availableThemesForNewGroup.length === 0 ?
                      'Nenhum tema disponível'
                    : 'Selecionar Tema'
                  }
                  options={availableThemesForNewGroup.map((theme) => ({
                    text: Str.from(theme.text).initCap,
                    value: theme.value,
                  }))}
                  loading={loadingThemesForGroup.get(String(newThemeGroup?.value)) || false}
                  disabled={!areSameBrand}>
                  <Selector.Icon value={Tag} color={!areSameBrand ? Tokens.ColorTextTertiary : Tokens.ColorUIPrimary} />
                </Selector.Default>
              </Flex>

              <Button.Root $variant="secondary" onClick={handleAddTheme} disabled={!areSameBrand}>
                <Button.Icon
                  value={Plus}
                  color={!areSameBrand ? Tokens.ColorTextTertiary : Tokens.ColorTextSecondary}
                />
                <Button.Text value="Adicionar" />
              </Button.Root>
            </Flex>
          </ThemeFormContainer>

          {/* Temas existentes das postagens selecionadas */}
          {allUniqueTags.length > 0 && areSameBrand && (
            <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
              <SectionLabel>Temas das postagens selecionadas</SectionLabel>
              {Object.entries(
                allUniqueTags
                  .filter((tag) => !themeTagsToRemove.some((r) => r.tempId === tag.tempId))
                  .reduce(
                    (acc, tag) => {
                      if (!acc[tag.themeGroupInternalID]) {
                        acc[tag.themeGroupInternalID] = { groupName: tag.themeGroupName, themes: [] }
                      }
                      acc[tag.themeGroupInternalID].themes.push(tag)
                      return acc
                    },
                    {} as Record<string, { groupName: string; themes: ThemeTag[] }>,
                  ),
              ).map(([groupId, groupData]) => (
                <Flex key={groupId} $type="column" $align="flex-start" $gap={6} $fit>
                  <ThemeGroupLabel>{groupData.groupName}:</ThemeGroupLabel>
                  <Flex $type="row" $align="center" $gap={8} style={{ flexWrap: 'wrap' }}>
                    {groupData.themes.map((tag) => (
                      <ThemeTag key={tag.tempId}>
                        <ThemeTagText title={tag.themeName}>{tag.themeName}</ThemeTagText>
                        <ThemeTagDeleteButton onClick={() => handleRemoveCommonTheme(tag.tempId)} title="Remover tema">
                          ×
                        </ThemeTagDeleteButton>
                      </ThemeTag>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          )}

          {/* Novos temas a adicionar */}
          <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={8}>
            {themeTagsToAdd.length > 0 && (
              <Flex $type="column" $justify="flex-start" $align="stretch" $fit $gap={12}>
                <SectionLabel $disabled={!areSameBrand}>Novos temas para adicionar</SectionLabel>

                {Object.entries(
                  themeTagsToAdd.reduce(
                    (acc, tag) => {
                      if (!acc[tag.themeGroupInternalID]) {
                        acc[tag.themeGroupInternalID] = {
                          groupName: tag.themeGroupName,
                          themes: [],
                        }
                      }
                      acc[tag.themeGroupInternalID].themes.push(tag)
                      return acc
                    },
                    {} as Record<string, { groupName: string; themes: ThemeTag[] }>,
                  ),
                ).map(([groupId, groupData]) => (
                  <Flex key={groupId} $type="column" $align="flex-start" $gap={6} $fit>
                    <ThemeGroupLabel>{groupData.groupName}:</ThemeGroupLabel>
                    <Flex $type="row" $align="center" $gap={8} style={{ flexWrap: 'wrap' }}>
                      {groupData.themes.map((tag) => (
                        <ThemeTag key={tag.tempId}>
                          <ThemeTagText title={tag.themeName}>{tag.themeName}</ThemeTagText>
                          <ThemeTagDeleteButton onClick={() => handleRemoveTheme(tag.tempId)} title="Remover tema">
                            ×
                          </ThemeTagDeleteButton>
                        </ThemeTag>
                      ))}
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            )}

            {areSameBrand && themeTagsToAdd.length === 0 && allUniqueTags.length === 0 && (
              <EmptyStateText>Nenhum tema selecionado. Os temas existentes serão mantidos.</EmptyStateText>
            )}
          </Flex>
        </Flex>

        <Flex $type="row" $justify="flex-end" $align="center" $fit $gap={16}>
          <Button.Root $variant="secondary" onClick={onClose} style={{ width: '7rem' }}>
            <Button.Text value="Cancelar" />
          </Button.Root>
          <Button.Root $variant="primary" onClick={handleBatchUpdate} $loading={isUpdating} style={{ width: '7rem' }}>
            <Button.Text value="Atualizar" />
          </Button.Root>
        </Flex>
      </Container>
    </Overlay>
  )
}
